'use client'

import { cx } from '@/app/lib/utils'
import { FieldMessage, useFieldIds } from '@/app/components/atoms/forms/field'
import { IconCheck, IconRemove } from '@/app/components/atoms/Icons'
import type { CheckboxProps } from './types'
import styles from './Checkbox.module.scss'

/**
 * Checkbox del DS: input nativo `type="checkbox"` asociado a su `<label>`,
 * con una caja visual custom. Controlado vía `checked`/`onChange`. Soporta
 * estado `indeterminate`. La a11y de etiqueta/mensaje se comparte con el resto
 * de campos del DS vía `useFieldIds` + `FieldMessage`.
 */
export function Checkbox({
  label,
  checked = false,
  indeterminate = false,
  disabled = false,
  errorText,
  helperText,
  size = 'm',
  inverse = false,
  onChange,
  id,
  name,
  value,
}: CheckboxProps) {
  const { fieldId, messageId } = useFieldIds(id)

  const hasError = Boolean(errorText)
  const hasMessage = Boolean(errorText || helperText)
  const isActive = checked || indeterminate
  const iconSize = size === 's' ? 12 : 14

  return (
    <div className={styles.root}>
      <label
        htmlFor={fieldId}
        className={cx(styles.control, inverse && styles.inverse, disabled && styles['control--disabled'])}
      >
        <span className={styles.boxWrap}>
          <input
            id={fieldId}
            name={name}
            value={value}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={hasMessage ? messageId : undefined}
            className={styles.input}
            onChange={(e) => onChange?.(e.target.checked)}
            ref={(el) => {
              if (el) el.indeterminate = indeterminate
            }}
          />
          <span
            aria-hidden="true"
            className={cx(
              styles.box,
              styles[`box--${size}`],
              isActive && styles['box--active'],
              hasError && styles['box--error'],
            )}
          >
            {indeterminate && !checked && <IconRemove size={iconSize} className={styles.glyph} />}
            {checked && <IconCheck size={iconSize} className={styles.glyph} />}
          </span>
        </span>

        <span className={styles.label}>{label}</span>
      </label>

      <FieldMessage id={messageId} error={errorText} helper={helperText} />
    </div>
  )
}
